'use client';

import { History, Search, TrendingUp, X } from 'lucide-react';
import { Button } from '@upzi/web-shell/ui/button';
import { Skeleton } from '@upzi/web-shell/ui/skeleton';
import { cn } from '@/lib/utils';

export type JobSearchSuggestionItem = {
  id: string;
  label: string;
  source: 'recent' | 'suggestion';
};

type JobSearchSuggestionsProps = {
  activeIndex: number;
  id: string;
  items: JobSearchSuggestionItem[];
  isLoading?: boolean;
  onClearRecent: () => void;
  onRemoveRecent: (value: string) => void;
  onSelect: (value: string) => void;
  query: string;
  variant?: 'desktop' | 'mobile';
};

function normalizeSearchValue(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLocaleLowerCase('vi-VN');
}

export function getJobSearchSuggestionItems(
  query: string,
  recentSearches: string[],
  hotKeywords: string[],
  maxSuggestions?: number
): JobSearchSuggestionItem[] {
  const normalizedQuery = normalizeSearchValue(query);
  const recentItems = normalizedQuery
    ? []
    : recentSearches.map((label, index) => ({
        id: `recent-${index}`,
        label,
        source: 'recent' as const,
      }));
  const suggestionLimit = maxSuggestions ?? (normalizedQuery ? 11 : 4);
  const seenSuggestions = new Set<string>();
  const suggestionItems: JobSearchSuggestionItem[] = [];

  for (const label of hotKeywords) {
    if (
      normalizedQuery &&
      !normalizeSearchValue(label).includes(normalizedQuery)
    ) {
      continue;
    }

    const normalizedLabel = normalizeSearchValue(label);

    if (seenSuggestions.has(normalizedLabel)) continue;

    seenSuggestions.add(normalizedLabel);
    suggestionItems.push({
      id: `suggestion-${suggestionItems.length}`,
      label,
      source: 'suggestion',
    });

    if (suggestionItems.length === suggestionLimit) break;
  }

  return [...recentItems, ...suggestionItems];
}

function HighlightedSuggestion({
  label,
  query,
}: {
  label: string;
  query: string;
}) {
  const normalizedQuery = normalizeSearchValue(query);

  if (!normalizedQuery) return label;

  const matchIndex = normalizeSearchValue(label).indexOf(normalizedQuery);

  if (matchIndex < 0) return label;

  const matchEnd = matchIndex + query.trim().length;

  return (
    <>
      {label.slice(0, matchIndex)}
      <strong className="font-semibold">
        {label.slice(matchIndex, matchEnd)}
      </strong>
      {label.slice(matchEnd)}
    </>
  );
}

export function JobSearchSuggestions({
  activeIndex,
  id,
  items,
  isLoading = false,
  onClearRecent,
  onRemoveRecent,
  onSelect,
  query,
  variant = 'desktop',
}: JobSearchSuggestionsProps) {
  const isMobile = variant === 'mobile';
  const recentItems = items.filter(item => item.source === 'recent');
  const suggestionItems = items.filter(item => item.source === 'suggestion');
  const isInputting = Boolean(query.trim());
  const showPills = isMobile && !isInputting && recentItems.length === 0;

  const renderItem = (item: JobSearchSuggestionItem) => {
    const itemIndex = items.findIndex(candidate => candidate.id === item.id);
    const isActive = itemIndex === activeIndex;
    const Icon =
      item.source === 'recent' ? History : isInputting ? Search : TrendingUp;

    return (
      <div
        key={item.id}
        className={cn(
          'group flex h-10 min-w-0 items-center rounded-[14px]',
          isMobile && isInputting && 'h-12',
          showPills &&
            'border-outline-variant h-9 w-fit max-w-full rounded-full border',
          isActive && 'bg-primary-variant text-primary'
        )}
      >
        <Button
          id={`${id}-${item.id}`}
          type="button"
          variant="ghost"
          role="option"
          aria-selected={isActive}
          onMouseDown={event => event.preventDefault()}
          onClick={() => onSelect(item.label)}
          className={cn(
            'text-on-surface hover:bg-primary-variant hover:text-primary group-hover:text-primary h-10 min-w-0 flex-1 justify-start gap-2 rounded-[14px] px-3 py-2 text-base leading-5 font-normal shadow-none',
            isMobile && 'text-ui-base',
            isMobile && isInputting && 'h-12',
            showPills && 'h-full rounded-full'
          )}
        >
          <Icon
            className={cn(
              'size-5 shrink-0',
              item.source === 'suggestion' && !isInputting && 'text-negative'
            )}
            aria-hidden
          />
          <span className="min-w-0 truncate text-left">
            <HighlightedSuggestion label={item.label} query={query} />
          </span>
        </Button>

        {item.source === 'recent' ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Xoá ${item.label} khỏi lịch sử tìm kiếm`}
            onMouseDown={event => event.preventDefault()}
            onClick={() => onRemoveRecent(item.label)}
            className="text-on-surface hover:bg-primary-variant hover:text-primary size-8 rounded-full shadow-none"
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <div
      id={id}
      role="listbox"
      aria-label="Gợi ý tìm kiếm việc làm"
      className={cn(
        'animate-in fade-in slide-in-from-top-1 w-full p-1 duration-150 motion-reduce:animate-none',
        isMobile && 'p-0'
      )}
    >
      {isLoading ? (
        <div
          className="space-y-1 p-1"
          role="status"
          aria-label="Đang tải gợi ý tìm kiếm"
        >
          {[0, 1, 2].map(index => (
            <div key={index} className="flex h-10 items-center gap-2.5 px-3">
              <Skeleton className="bg-surface-variant size-4 shrink-0 rounded-full motion-reduce:animate-none" />
              <Skeleton className="bg-surface-variant h-4 w-2/3 motion-reduce:animate-none" />
            </div>
          ))}
        </div>
      ) : null}

      {recentItems.length > 0 ? (
        <div className={cn('p-1', isMobile && 'p-0 pb-4')}>
          <div className="flex h-9 items-center justify-between">
            <p
              className={cn(
                'text-base-muted-foreground px-3 text-xs leading-5 font-normal',
                isMobile &&
                  'text-content-strong text-display-2xs leading-6 font-semibold'
              )}
            >
              Tìm kiếm gần đây
            </p>
            <Button
              type="button"
              variant="ghost"
              onMouseDown={event => event.preventDefault()}
              onClick={onClearRecent}
              className="text-primary hover:bg-primary-variant h-9 rounded-xl px-3 py-2 text-[13px] leading-5 font-semibold shadow-none"
            >
              Xoá tất cả
            </Button>
          </div>
          {recentItems.map(renderItem)}
        </div>
      ) : null}

      {suggestionItems.length > 0 ? (
        <div className={cn('p-1', isMobile && 'p-0')}>
          {!isInputting ? (
            <p
              className={cn(
                'text-base-muted-foreground flex h-[30px] items-center px-3 text-xs leading-5 font-normal',
                isMobile &&
                  'text-content-strong text-display-2xs mb-4 h-9 leading-6 font-semibold'
              )}
            >
              Gợi ý tìm kiếm
            </p>
          ) : null}
          <div className={cn(showPills && 'flex flex-col items-start gap-2')}>
            {suggestionItems.map(renderItem)}
          </div>
        </div>
      ) : null}
    </div>
  );
}
